import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";

/**
 * Typed, URL-search-param-backed state for the admin pages.
 *
 * Design: the URL is the source of truth. UI writes to the URL (setValues / reset),
 * an effect on the page reads the URL back into the MobX store and triggers loads.
 * `encode` omits any value equal to its schema default, so URLs stay short and
 * "is this filtered?" is trivial to answer.
 */

type ParamDef<T> = {
  default: T;
  parse?: (raw: string) => T;          // string -> value (identity for plain strings)
  serialize?: (value: T) => string;    // value -> string (String() by default)
};
type Schema = Record<string, ParamDef<any>>;
type Values<S extends Schema> = { [K in keyof S]: S[K]["default"] };

export function decode<S extends Schema>(params: URLSearchParams, schema: S): Values<S> {
  const out: any = {};
  for (const key in schema) {
    const raw = params.get(key);
    out[key] = raw === null
      ? schema[key].default
      : (schema[key].parse ? schema[key].parse(raw) : raw);
  }
  return out;
}

export function encode<S extends Schema>(values: Values<S>, schema: S): URLSearchParams {
  const params = new URLSearchParams();
  for (const key in schema) {
    const def = schema[key];
    const value = (values as any)[key];
    if (value === undefined || value === null) continue;
    if (value === def.default) continue;                 // omit defaults -> short URLs
    const raw = def.serialize ? def.serialize(value) : String(value);
    if (raw === "") continue;
    params.set(key, raw);
  }
  return params;
}

/**
 * Read the URL as typed state and write single/multiple params (default-omitting).
 * `setValues` merges the patch over the current URL, so callers only pass what changed.
 * `reset` clears the schema's keys back to their defaults (keeping any `keep` keys).
 */
export function useUrlState<S extends Schema>(schema: S) {
  const [params, setParams] = useSearchParams();
  const values = useMemo(() => decode(params, schema), [params, schema]);

  const setValues = useCallback(
    (patch: Partial<Values<S>>, opts?: {replace?: boolean}) => {
      setParams(
        prev => encode({...decode(prev, schema), ...patch}, schema),
        {replace: opts?.replace ?? false},
      );
    },
    [setParams, schema],
  );

  // Clear all filters. Not wired to any button yet — the customer is expected to
  // want a "počisti filtre" button later. Usage:
  //   const {reset} = useUrlState(participantsUrlSchema);
  //   <AdminButton onClick={() => reset(['osebe'])}>POČISTI FILTRE</AdminButton>
  // Pass keys in `keep` to preserve them (e.g. ['osebe'] keeps the open person);
  // call reset() with no args to clear everything.
  const reset = useCallback(
    (keep: (keyof S)[] = [], opts?: {replace?: boolean}) => {
      const patch: any = {};
      for (const key in schema) {
        if (!keep.includes(key as keyof S)) patch[key] = schema[key].default;
      }
      setValues(patch, opts);
    },
    [setValues, schema],
  );

  return {values, setValues, reset, params};
}

/**
 * Build a `?tip=..&tecaj=..` query (or '' when both empty) to carry the shared
 * tip/tecaj selection when navigating between the admin pages. Both pages read
 * these keys from their URL on mount, so the selection persists across the jump.
 */
export function tipTecajQuery(tip?: string, tecaj?: string): string {
  const p = new URLSearchParams();
  if (tip) p.set('tip', tip);
  if (tecaj) p.set('tecaj', tecaj);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/** /admin/prijavljene-osebe — filters + open person. */
export const participantsUrlSchema = {
  tip:     {default: undefined as string | undefined},
  tecaj:   {default: undefined as string | undefined},
  pogoji:  {default: undefined as string | undefined}, // dodatniPogoji
  placilo: {default: undefined as string | undefined}, // dodatniPlacilniPogoji
  search:  {default: undefined as string | undefined},
  sort:    {default: "-date_created"},
  page:    {default: 0, parse: (r: string) => (Number.isFinite(+r) ? +r : 0)},
  date:    {
    default: undefined as Date | undefined,
    parse: (r: string) => (Number.isFinite(+r) ? new Date(+r) : undefined),
    serialize: (d: Date) => String(d.getTime()),
  },
  osebe:   {default: undefined as string | undefined}, // selected participant sessionId
};

/** /admin/ (calendar) — filters + viewed month + optional chosen day. */
export const calendarUrlSchema = {
  tip:   {default: undefined as string | undefined},
  tecaj: {default: undefined as string | undefined},
  month: {default: undefined as string | undefined}, // "YYYY-MM"
  dan:   {default: undefined as string | undefined}, // "YYYY-MM-DD"
};